import List from "@material-ui/core/List";
import React, {ChangeEvent, useEffect, useState} from "react";
import {
    getOrganizationUnit,
    getOrganizationUnitChildren,
    getTopOrganizationUnits
} from "../../../api/organization-unit-functions";
import ListItem from "@material-ui/core/ListItem";
import {ListItemSecondaryAction, ListItemText} from "@material-ui/core";
import Radio from "@material-ui/core/Radio";
import {ArrowBackIos, ArrowForwardIos} from "@material-ui/icons";
import Divider from "@material-ui/core/Divider";
import {makeStyles} from "@material-ui/core/styles";
import {GreyedOutProgressMask} from "../../ui/GreyedOutProgressMask";
import {run} from "../../../utils/function-util";
import {OrganizationUnit, OrgUnit} from "../../../models/organization";

const useStyles = makeStyles(theme => ({
    OrganizationUnitRadioBtn: {
        boxSizing: 'border-box',
        paddingLeft: '25px',
    },
    ChildOrganizationUnit: {
        paddingLeft: theme.spacing(4)
    }
}));

interface Props {
    selectedId?: number,
    organizationId: number,
    onSelectedOrganizationUnitChanged: (orgUnit?: OrgUnit) => void,
    onProcessing?: (value: boolean) => void
}

export const OrganizationUnitSelector = ({selectedId, onSelectedOrganizationUnitChanged, onProcessing, organizationId}: Props) => {
    const classes = useStyles();
    const [organizationUnit, setOrganizationUnit] = useState<OrganizationUnit>();
    const [organizationUnitChildren, setOrganizationUnitChildren] = useState<OrganizationUnit[]>([]);
    const [parentOrganizationUnit, setParentOrganizationUnit] = useState<OrganizationUnit | undefined>();
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [topsIdsWithNames, setTopsIdsWithNames] = useState<OrgUnit[]>([]);

    useEffect(run(
        async () => {
            raiseProcessing(true);
            if (organizationId) {
                // Discard pre-selection if organization changed
                if(selectedId && organizationUnit) {
                    onSelectedOrganizationUnitChanged(undefined);
                }
                resetState();
                return await getTopOrganizationUnits(organizationId);
            }
            return Promise.resolve([])
        }, (tops: OrganizationUnit[]) => {
            if (tops && tops.length) {
                setTopsIdsWithNames(tops.map((top) => ({
                    id: top.id,
                    name: top.name
                })));
            }
            raiseProcessing(false);

        }), [organizationId]);

    useEffect(run(async () => {
        if (!organizationUnit) {
            raiseProcessing(true);
            if (selectedId) {
                return await getNewOrganizationUnitWithParent(selectedId);
            }
            if (topsIdsWithNames && topsIdsWithNames[0] && topsIdsWithNames[0].id) {
                return await getNewOrganizationUnitWithParent(topsIdsWithNames[0].id);
            }
        }
    }, ({parent, orgUnit}: { parent: OrganizationUnit | undefined, orgUnit: OrganizationUnit }) => {
        if (!organizationUnit)
            setNewOrgUnit({parent, orgUnit});
    }, undefined, () => {
        raiseProcessing(false);
    }), [selectedId, topsIdsWithNames]);

    useEffect(run(
        async () => (organizationUnit) ? await getOrganizationUnitChildren(organizationId, organizationUnit?.id) :
            Promise.resolve([]), setOrganizationUnitChildren
        ),
        [organizationUnit]);

    const raiseProcessing = (value: boolean) => {
        setIsProcessing(value);
        if (onProcessing) {
            onProcessing(value);
        }
    };

    const getNewOrganizationUnitWithParent = async (newOpenedOrgUnitId: number): Promise<{ parent: OrganizationUnit | undefined, orgUnit: OrganizationUnit }> => {
        raiseProcessing(true);
        const orgUnit = await getOrganizationUnit(organizationId, newOpenedOrgUnitId);
        let parent;
        if (topsIdsWithNames.findIndex((top: OrgUnit) => top.id === orgUnit.id) === -1) {
            parent = await getOrganizationUnit(organizationId, orgUnit.parentId);
        }
        return {parent, orgUnit};
    };

    const setNewOrgUnit = ({parent, orgUnit}: { parent: OrganizationUnit | undefined, orgUnit: OrganizationUnit }) => {
        setParentOrganizationUnit(parent);
        setOrganizationUnit(orgUnit);
        onSelectedOrganizationUnitChanged({id: orgUnit.id, name: orgUnit.name});
        raiseProcessing(false);
    };

    const changeSelectedOrgUnit = (event: ChangeEvent<HTMLInputElement>, newlySelectedOrganization: OrgUnit) => {
        if (event.target.checked) {
            onSelectedOrganizationUnitChanged({
                id: newlySelectedOrganization.id,
                name: newlySelectedOrganization.name
            });
        }
    };

    const resetState = () => {
        setOrganizationUnitChildren([]);
        setParentOrganizationUnit(undefined);
        setOrganizationUnit(undefined);
    }

    return (<GreyedOutProgressMask isActive={isProcessing} displaySpinner={true}>
        {organizationUnit ?
            <List>
                {   parentOrganizationUnit &&
                    <ListItem button
                              onClick={async () => setNewOrgUnit(await getNewOrganizationUnitWithParent(parentOrganizationUnit.id))}>
                        <ArrowBackIos color="primary" fontSize="small"/>
                        <ListItemText primary={parentOrganizationUnit.name} primaryTypographyProps={{variant: 'body2'}}/>
                    </ListItem>
                }
                <ListItem>
                    <ListItemText primary={`${organizationUnit.name}`} primaryTypographyProps={{variant: 'body2'}}/>
                    <ListItemSecondaryAction>
                        <Radio checked={organizationUnit?.id === selectedId}
                               edge='end'
                               color="primary"
                               onChange={(event) => changeSelectedOrgUnit(event, organizationUnit)}/>
                    </ListItemSecondaryAction>
                </ListItem>
                <Divider/>
                {organizationUnitChildren &&
                <React.Fragment>
                    {organizationUnitChildren.map(child => <ListItem button
                                                                     onClick={async () => setNewOrgUnit(await getNewOrganizationUnitWithParent(child.id))}
                                                                     className={classes.ChildOrganizationUnit}
                                                                     key={`org-unit-selector-child-id-${child.id}`}>
                        <ListItemText primary={child.name} primaryTypographyProps={{variant: 'body2'}}/>
                        <ArrowForwardIos color="primary" fontSize="small"/>
                        <ListItemSecondaryAction className={classes.OrganizationUnitRadioBtn}>
                            <Radio checked={child.id === selectedId}
                                   edge='end'
                                   color="primary"
                                   onChange={(event) => changeSelectedOrgUnit(event, child)}/>
                        </ListItemSecondaryAction>
                    </ListItem>)}
                </React.Fragment>}
                {!parentOrganizationUnit &&
                topsIdsWithNames.map((top) => {
                    if (top.id !== organizationUnit.id) {
                        return <React.Fragment key={`top-org-unit-selector-${top.id}`}>
                            <ListItem button
                                      onClick={async () => setNewOrgUnit(await getNewOrganizationUnitWithParent(top.id))}
                                      key={`org-unit-selector-top-id-${top.id}`}>
                                <ListItemText primary={top.name} primaryTypographyProps={{variant: 'body1'}}/>
                                <ArrowForwardIos color="primary" fontSize="small"/>
                                <ListItemSecondaryAction>
                                    <Radio checked={top.id === selectedId} edge='end'
                                           color="primary"
                                           onChange={(event) => changeSelectedOrgUnit(event, top)}/>
                                </ListItemSecondaryAction>
                            </ListItem>
                        </React.Fragment>;
                    } else {
                        return <React.Fragment key={`org-unit-selector-top-id-${top.id}`}/>;
                    }
                })}
            </List>
            : null}
    </GreyedOutProgressMask>);
};
